import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Skeleton, Chip, List, ListItem, ListItemText, ListItemIcon, Divider } from '@mui/material';
import { Person as NPCIcon } from '@mui/icons-material';
import { DATABASE_SERVICE_URL } from '../../services/workerUrls';
import { logService } from '../../services/logService';
import type { NPC, Player } from '../../types/gameState';

interface NPCsPanelProps {
  playerId: string;
}

export function NPCsPanel({ playerId }: NPCsPanelProps) {
  const [npcs, setNpcs] = useState<NPC[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [location, setLocation] = useState<string>('');

  useEffect(() => {
    async function fetchNPCs() {
      setIsLoading(true);
      setError(null);

      try {
        // First get player location
        const playerLogId = logService.api('GET', `/api/players/${playerId}`, {}, 'pending');
        const playerStart = Date.now();
        const playerRes = await fetch(`${DATABASE_SERVICE_URL()}/api/players/${playerId}`);
        const playerDuration = Date.now() - playerStart;

        if (!playerRes.ok) {
          logService.updateLog(playerLogId, { status: 'error', duration: playerDuration, message: `HTTP ${playerRes.status}` });
          setError('Failed to load player data');
          setIsLoading(false);
          return;
        }

        const playerResult = await playerRes.json();
        if (!playerResult.success || !playerResult.data) {
          logService.updateLog(playerLogId, { status: 'error', duration: playerDuration, message: 'No player data' });
          setError('No player data available');
          setIsLoading(false);
          return;
        }

        logService.updateLog(playerLogId, { status: 'success', duration: playerDuration });
        const player: Player = playerResult.data;

        // Fetch NPCs at player's location
        let npcsEndpoint = '';
        if (player.current_station_id) {
          npcsEndpoint = `/api/npcs?station=${player.current_station_id}`;
          setLocation('at this station');
        } else if (player.current_system_id) {
          npcsEndpoint = `/api/npcs?system=${player.current_system_id}`;
          setLocation('in this system');
        } else {
          setNpcs([]);
          setIsLoading(false);
          return;
        }

        const npcsLogId = logService.api('GET', npcsEndpoint, {}, 'pending');
        const npcsStart = Date.now();
        const npcsRes = await fetch(`${DATABASE_SERVICE_URL()}${npcsEndpoint}`);
        const npcsDuration = Date.now() - npcsStart;

        if (!npcsRes.ok) {
          logService.updateLog(npcsLogId, { status: 'error', duration: npcsDuration, message: `HTTP ${npcsRes.status}` });
          setError('Failed to load NPCs');
        } else {
          const npcsResult = await npcsRes.json();
          if (npcsResult.success && npcsResult.data) {
            logService.updateLog(npcsLogId, { status: 'success', duration: npcsDuration });
            setNpcs(npcsResult.data);
          } else {
            logService.updateLog(npcsLogId, { status: 'error', duration: npcsDuration, message: 'No NPCs data' });
            setNpcs([]);
          }
        }
      } catch (err) {
        setError('Failed to load NPCs');
      } finally {
        setIsLoading(false);
      }
    }

    fetchNPCs();
  }, [playerId]);

  if (isLoading) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="rectangular" height={150} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="error" align="center">
                {error}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="panel">
      <Box className="panel-scroll">
        <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="overline" sx={{ color: 'info.main', fontWeight: 700, letterSpacing: 1, fontSize: '0.75rem', display: 'block', mb: 1 }}>
              Nearby Characters
            </Typography>

            {npcs.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <NPCIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No one nearby {location}
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {npcs.length} character{npcs.length > 1 ? 's' : ''} {location}
                </Typography>
                <List sx={{ p: 0 }}>
                  {npcs.map((npc: NPC, index: number) => (
                    <Box key={npc.id}>
                      {index > 0 && <Divider />}
                      <ListItem sx={{ px: 1, py: 1.5, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                          <NPCIcon
                            sx={{
                              fontSize: 20,
                              color:
                                npc.npc_type === 'pirate'
                                  ? 'error.main'
                                  : npc.npc_type === 'quest_giver'
                                    ? 'warning.main'
                                    : npc.npc_type === 'trader'
                                      ? 'success.main'
                                      : 'info.main',
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {npc.name}
                              </Typography>
                              <Chip
                                label={npc.npc_type.replace('_', ' ')}
                                size="small"
                                color={
                                  npc.npc_type === 'pirate'
                                    ? 'error'
                                    : npc.npc_type === 'quest_giver'
                                      ? 'warning'
                                      : npc.npc_type === 'trader'
                                        ? 'success'
                                        : 'default'
                                }
                                sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              {npc.title && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                  {npc.title}
                                </Typography>
                              )}
                              {npc.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {npc.description}
                                </Typography>
                              )}
                              {npc.faction_id && (
                                <Chip
                                  label={npc.faction_id.replace('fac-', '').replace(/-/g, ' ')}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: '0.6rem', mt: 0.5, textTransform: 'capitalize' }}
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    </Box>
                  ))}
                </List>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
